import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import Layout from '../components/Layout';
import Header from '../components/Header';
import Metrics from '../components/metrics/Metrics';
import {
    api,
    BaseImpact,
    ExternalTripCountsData,
    isVehicleClass,
    Settings,
    VehicleClass,
} from '../data/api';
import { useQueryState } from '../utils/use-query-state';

import styles from '../styles/Dashboard.module.scss';
import React, { useMemo, useRef, useState } from 'react';
import DataSelector from '../components/DataSelector';
import Icon from '../components/Icon';
import { OperatorName } from '../components/OperatorLogo';
import AboutThisData from '../components/AboutThisData';
import { generateVehicleClasses } from '../data/available-vehicle-classes';
import { generateTimeRanges } from '../data/available-time-ranges';
import ActiveOperators from '../components/ActiveOperators';
import ImpactList from '../components/impact/ImpactList';
import Link from 'next/link';
import {
    externalTripCountsMetatdataSectionId,
    getExternalCountForPeriod,
} from '../utils/external-trip-counts';

const Map = dynamic(() => import('../components/map/Map'), { ssr: false });

interface DashboardProps {
    hasHighInjuryData: boolean;
    settings: Settings;
    timePeriodsAvailableByVehicleClass: TimePeriodsByVehicleClass;
    impacts: BaseImpact[];
    externalTripCounts: ExternalTripCountsData | null;
}

function Dashboard({
    hasHighInjuryData,
    settings,
    timePeriodsAvailableByVehicleClass,
    impacts,
    externalTripCounts,
}: DashboardProps) {
    const [timeRange, setTimeRange] = useQueryState('time', 'all');
    const [vehicleClass, setVehicleClass] = useQueryState('vehicle', 'all');
    const sectionContainerRef = useRef<HTMLDivElement>(null);
    const impactListRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<any>(null);

    const showBannerMessage: boolean = useMemo(() => {
        if (!externalTripCounts || !externalTripCounts.banner_message) {
            return false;
        }
        return (
            getExternalCountForPeriod(
                timeRange,
                vehicleClass as VehicleClass,
                externalTripCounts.tripCounts
            ) > 0
        );
    }, [externalTripCounts, timeRange, vehicleClass]);

    // for new dashboards, we may not have data generated yet.
    if (Object.keys(timePeriodsAvailableByVehicleClass).length === 0) {
        // if we're on production - throw an error to Rollbar. We should not get
        // here, but if we have, then change the PublicDashboardSettings in admin
        // to disable this dashboard for production.
        if (process.env.NEXT_PUBLIC_DASHBOARD_ENVIRONMENT === 'production') {
            if (window.rollbar) {
                window.rollbar.error(
                    new Error(
                        `No metrics data is available for ${settings.name}.`
                    )
                );
            }
        }

        return (
            <Layout
                header={<Header settings={settings} map={map} />}
                title={`${settings.region_title} | Micromobility Dashboard`}
            >
                <div className={styles.noDataNotice}>
                    No data available at this time.
                    {process.env.NEXT_PUBLIC_DASHBOARD_ENVIRONMENT !==
                    'production'
                        ? ' If this dashboard is newly created, it may take up to a day to generate data.'
                        : null}
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            header={<Header settings={settings} map={map} />}
            title={`${settings.region_title} | Micromobility Dashboard`}
        >
            <div
                className={cx(
                    'rideReportContainer',
                    'shadow-8',
                    styles.desktopSelectors
                )}
            >
                <DataSelector
                    timePeriodsAvailableByVehicleClass={
                        timePeriodsAvailableByVehicleClass
                    }
                    timeRange={timeRange}
                    setTimeRange={setTimeRange}
                    vehicleClass={vehicleClass}
                    setVehicleClass={setVehicleClass}
                />
            </div>
            <div className={styles.pageContainer}>
                <section className={styles.map}>
                    <Map
                        settings={settings}
                        timeRange={timeRange}
                        vehicleClass={vehicleClass}
                        setMap={setMap}
                    />
                </section>

                <div
                    style={{ scrollMarginTop: '64px' }}
                    ref={sectionContainerRef}
                    onClick={() => {
                        sectionContainerRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                            inline: 'nearest',
                        });
                    }}
                    className={styles.outerSectionContainer}
                >
                    <div className="relative">
                        {impacts.length > 0 ? (
                            <div className="absolute top-[-20px] w-full">
                                <Link href="#impacts" passHref>
                                    <div className="mx-auto w-36 py-2 rounded-md text-center bg-gray-00 hover:cursor-pointer font-semibold">
                                        <a className="after:absolute after:w-2 after:h-2 after:rounded-full after:bg-[#00E396] after:ml-1">
                                            Impacts
                                        </a>
                                    </div>
                                </Link>
                            </div>
                        ) : (
                            <hr className={styles.mobileDragBar} />
                        )}
                        {/* only shown on mobile */}
                        <div className={styles.location}>
                            <Icon
                                icon="Location"
                                className={styles.locationIcon}
                            />
                            {settings.region_location_name}
                        </div>
                        {/* only shown on mobile */}
                        <div className={styles.mobileSelectors}>
                            <DataSelector
                                timePeriodsAvailableByVehicleClass={
                                    timePeriodsAvailableByVehicleClass
                                }
                                timeRange={timeRange}
                                setTimeRange={setTimeRange}
                                vehicleClass={vehicleClass}
                                setVehicleClass={setVehicleClass}
                            />
                        </div>
                    </div>

                    <div className={styles.mainContentContainer}>
                        {externalTripCounts && showBannerMessage && (
                            <div className={styles.externalTripsBanner}>
                                {/* Insert a link element into correct place in banner text */}
                                {
                                    externalTripCounts.banner_message.split(
                                        '?<metadataSectionLink>?'
                                    )[0]
                                }
                                <Link
                                    href={
                                        '#' +
                                        externalTripCountsMetatdataSectionId
                                    }
                                    passHref
                                >
                                    <a>here</a>
                                </Link>
                                {
                                    externalTripCounts.banner_message.split(
                                        '?<metadataSectionLink>?'
                                    )[1]
                                }
                            </div>
                        )}
                        <Metrics
                            timeRange={timeRange}
                            vehicleClass={vehicleClass as VehicleClass}
                            citySettings={settings}
                            externalTripCounts={externalTripCounts}
                        />
                        <div
                            id="impacts"
                            ref={impactListRef}
                            style={{ scrollMarginTop: '96px' }}
                        >
                            <ImpactList
                                impacts={impacts}
                                locationName={settings.name}
                            />
                        </div>

                        <section className={styles.sectionContainer}>
                            <div
                                className={cx(
                                    styles.section,
                                    styles.orgInfoContainer
                                )}
                            >
                                {settings.organization_logo_url && (
                                    <img
                                        alt="agency logo"
                                        src={settings.organization_logo_url}
                                        className={styles.image}
                                    />
                                )}
                                <div className={styles.contactInfoContainer}>
                                    <h1>{settings.region_title}</h1>
                                    <h2>
                                        {settings.organization_display_name}
                                    </h2>
                                    <p
                                        className={cx(
                                            styles.text,
                                            styles.contactInfo
                                        )}
                                    >
                                        <span>
                                            {
                                                settings.organization_address_line_one
                                            }
                                        </span>
                                        <span>
                                            {
                                                settings.organization_address_line_two
                                            }
                                        </span>
                                        <a
                                            className={styles.link}
                                            href={`mailto:${settings.organization_contact_email}`}
                                        >
                                            {
                                                settings.organization_contact_email
                                            }
                                        </a>
                                    </p>

                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={settings.organization_url}
                                        className={styles.openWebsiteButton}
                                    >
                                        Open Website
                                    </a>
                                </div>
                            </div>

                            {hasHighInjuryData && (
                                <div className={styles.section}>
                                    <img
                                        alt="vision zero"
                                        src="/vision-zero.png"
                                        className={styles.image}
                                    />
                                    <div>
                                        <h1>Vision Zero Data</h1>
                                        <p className={styles.text}>
                                            Vision Zero is a strategy to improve
                                            public safety and eliminate all
                                            traffic fatalities and severe
                                            injuries. The layer included in the
                                            map identifies areas where severe
                                            and fatal injuries were most
                                            concentrated based on injury counts.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section>
                            <AboutThisData
                                externalTripCounts={externalTripCounts}
                            />
                        </section>

                        <ActiveOperators
                            activeOperators={
                                settings.currently_active_operators as OperatorName[]
                            }
                            locationName={settings.region_location_name}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export interface TimeRangeListItem {
    key: string;
    name: string;
}

export type TimePeriodsByVehicleClass = Record<string, TimeRangeListItem[]>;

export async function getStaticProps(context: GetStaticPropsContext) {
    const slug = context.params?.slug;
    if (typeof slug !== 'string') {
        throw new Error(`Invalid slug ${slug}`);
    }

    const settings = await api.settings(slug);

    if (!settings) {
        return { notFound: true };
    }

    let externalTripCounts: ExternalTripCountsData | null = null;
    const hasHighInjuryData = await api.hasHighInjuryNetwork(slug);

    const timePeriodsAvailableByVehicleClass: TimePeriodsByVehicleClass = {};

    // get available vehicle classes
    let vehicleClasses = await generateVehicleClasses(slug);

    if (settings.apply_external_trip_counts) {
        externalTripCounts = await api.getExternalTripCounts(slug);

        // Add any vehicle classes that only appear external trip counts
        Object.keys(externalTripCounts.tripCounts).forEach(vc_string => {
            if (
                isVehicleClass(vc_string) &&
                !vehicleClasses.includes(vc_string)
            ) {
                vehicleClasses.push(vc_string);
            }
        });
    }
    for (let vc of vehicleClasses) {
        // for each vehicle class, get all of the time periods that exist
        timePeriodsAvailableByVehicleClass[vc] = await generateTimeRanges(
            settings.earliest_available_metrics,
            slug,
            vc,
            externalTripCounts
        );
    }

    const impacts = await api.impacts(slug);

    return {
        props: {
            settings,
            hasHighInjuryData,
            timePeriodsAvailableByVehicleClass,
            impacts,
            externalTripCounts,
        },
    };
}

export async function getStaticPaths() {
    const dashboards = await api.allDashboardSlugs();

    const paths = dashboards.map(dash => `/${dash.slug}`);

    return {
        paths,
        fallback: false,
    };
}

export default Dashboard;
